import React, { PropsWithChildren, useState } from "react";
import { Button, Divider } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import AppForm from "../../components/form/Form";
import agent from "../../app/api/agent";
import useToaster from "../../app/hooks/useToaster";
import FormModal from "../../components/FormModal";
import { useAppForm } from "../../components/form/useForm";
import { Internship, InternshipCreateFormModel } from "./types";
import { getCreateRequestPayload } from "./utils";
import InternshipForm from "./InternshipForm";
import DuplicateInternshipDropdown from "./DuplicateInternshipDropdown";

interface Props {
  onCreate?: (id: number) => void;
}

const InternshipCreateModal = ({ children: trigger, onCreate }: PropsWithChildren<Props>) => {
  const [isOpen, setOpen] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const { t } = useTranslation();
  const { success, apiError } = useToaster();
  const formHook = useAppForm({ defaultValues: emptyInternshipModel });
  const isDisabled = isSaving;

  const clearForm = () => formHook.reset(emptyInternshipModel);

  const close = () => {
    clearForm();
    setOpen(false);
  };

  const handleSubmit = async (model: InternshipCreateFormModel) => {
    setSaving(true);
    try {
      const id = await agent.Internships.create(getCreateRequestPayload(model));
      success("internship.form.successfully_created");
      onCreate?.(id);
      close();
    } catch (error) {
      apiError(error);
    } finally {
      setSaving(false);
    }
  };

  const prefillData = (internship: Internship) => {
    formHook.setValue("title", internship.title);
    formHook.setValue("description", internship.description);
    formHook.setValue("participantsCapacity", internship.participantsCapacity);
    formHook.setValue("remote", internship.remote);
    formHook.setValue("industryIds", internship.industryIds ?? [], { shouldDirty: true });
    formHook.setValue(
      "responsiblePerson.fullName",
      `${internship.responsiblePerson.firstName} ${internship.responsiblePerson.surname}`
    );
    formHook.setValue("responsiblePerson.jobTitle", internship.responsiblePerson.jobTitle);
    formHook.setValue("responsiblePerson.email", internship.responsiblePerson.email);
    formHook.setValue("responsiblePerson.phoneNumber", internship.responsiblePerson.phoneNumber);
  };

  const actions = (
    <>
      <Button primary loading={isSaving} disabled={isDisabled} onClick={formHook.handleSubmit(handleSubmit)}>
        {t("internship.form.create_button")}
      </Button>
      <Button negative disabled={isDisabled} onClick={close}>
        {t("common.actions.cancel")}
      </Button>
    </>
  );

  return (
    <FormModal
      title="internship.create_internship"
      {...{ isDisabled, isOpen, actions, trigger }}
      onClose={close}
      onOpen={() => setOpen(true)}
    >
      <AppForm formHook={formHook} isDisabled={isDisabled} onSubmit={handleSubmit}>
        <DuplicateInternshipDropdown onChange={prefillData} />
        <Divider />
        <InternshipForm />
      </AppForm>
    </FormModal>
  );
};

export default InternshipCreateModal;

const emptyInternshipModel: InternshipCreateFormModel = {
  title: "",
  city: "",
  address: "",
  participantsCapacity: null!,
  remote: false,
  startDate: "",
  endDate: "",
  description: "",
  useHqAddress: true,
  industryIds: [],
  responsiblePerson: {
    fullName: "",
    jobTitle: "",
    email: "",
    phoneNumber: "",
  },
};
